import { Box, Grid, MenuItem, TextField } from '@mui/material';
import type React from 'react';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import countries from '../../assets/countries.json';
import { useTranslation } from 'react-i18next';
interface DetailsEnterpriseProps {
  onValidChange?: (valid: boolean) => void;
}

export interface DetailsEnterpriseData {
  name: string;
  category: string;
  email: string;
  phone: string;
  address: string;
  country: string;
  locale: string;
  website: string;
  city: string;
}

const DetailsEnterprise = forwardRef<{ getEnterPriseData: () => void }, DetailsEnterpriseProps>(
  ({ onValidChange }, ref) => {
    const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
    const [formData, setFormData] = useState({
      name: '',
      registrationNumber: '',
      vatNumber: '',
      category: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      country: '',
      website: '',
    });
    const {t} = useTranslation();

    useEffect(() => {
      if (validationErrors.name || validationErrors.email || !formData?.name || !formData?.email) {
        onValidChange?.(true);
      } else {
        onValidChange?.(false);
      }
    }, [validationErrors, onValidChange, formData?.name, formData?.email]);

    // Handle input changes for text fields
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      // Validation
      if (name === 'name') {
        setValidationErrors((prev) => ({
          ...prev,
          name: value.trim() === '' ? 'Name is required' : '',
        }));
      }

      if (name === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        let error = '';
        if (!value.trim()) error = 'Email is required';
        else if (!emailRegex.test(value)) error = 'Enter a valid email';
        setValidationErrors((prev) => ({ ...prev, email: error }));
      }
    };

    useImperativeHandle(ref, () => ({
      getEnterPriseData: (): DetailsEnterpriseData => {
        return {
          name: formData.name || '',
          category: formData.category || '',
          email: formData.email || '',
          phone: formData.phone || '',
          address: formData.address || '',
          country: formData.country || '',
          locale: `fr-${formData.country}` || '',
          website: formData.website || '',
          city: formData.city || ''
        };
      },
    }));

    return (
      <Box
        ref={ref}
        component='form'
        sx={{
          '& .MuiTextField-root': { m: 1, width: '25ch' },
          mt: 2,
        }}
        noValidate
        autoComplete='off'
      >
        <Grid container spacing={2}>
          <Grid size={6}>
            <TextField
              style={{ width: '100%' }}
              name='name'
              label={t("createMerchant.form.companyName")}
              size='small'
              value={formData.name}
              InputLabelProps={{ shrink: !!formData.name }}
              onChange={handleChange}
              error={!!validationErrors.name}
              helperText={validationErrors.name}
              autoFocus
              autoComplete='off'
            />
          </Grid>

          <Grid size={6}>
            <TextField
              style={{ width: '100%' }}
              name='email'
              label={t("createMerchant.form.companyEmail")}
              size='small'
              value={formData.email}
              InputLabelProps={{ shrink: !!formData.email }}
              onChange={handleChange}
              error={!!validationErrors.email}
              helperText={validationErrors.email}
              autoComplete='off'
            />
          </Grid>

          <Grid size={6}>
            <TextField
              style={{ width: '100%' }}
              name='address'
              label={t("createMerchant.form.companyAddress")}
              size='small'
              value={formData.address}
              InputLabelProps={{ shrink: !!formData.address }}
              onChange={handleChange}
              autoComplete='off'
            />
          </Grid>

          <Grid size={6}>
            <TextField
              style={{ width: '100%' }}
              name='city'
              label={t("createMerchant.form.companyCity")}
              size='small'
              value={formData.city}
              InputLabelProps={{ shrink: !!formData.city }}
              onChange={handleChange}
              autoComplete='off'
            />
          </Grid>

          <Grid size={6}>
            <TextField
              style={{ width: '100%' }}
              name='phone'
              label={t("createMerchant.form.companyPhone")}
              size='small'
              type='tel'
              value={formData.phone}
              onChange={handleChange}
              placeholder='+221743058000'
              inputProps={{
                pattern: '^\\+?[0-9\\s-]{7,15}$',
                title: 'Enter a valid phone number (e.g., +221743058000)',
              }}
              InputLabelProps={{ shrink: !!formData.phone }}
              autoComplete='off'
            />
          </Grid>

          <Grid size={6}>
            <TextField
              style={{ width: '100%' }}
              name='country'
              label={t("createMerchant.form.companyCountry")}
              size='small'
              select
              value={formData.country}
              onChange={handleChange}
              autoComplete='off'
            >
              {countries.map(({ name = '', code = '' }) => (
                <MenuItem key={code} value={code}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={6}>
            <TextField
              style={{ width: '100%' }}
              name='registrationNumber'
              label={t("createMerchant.form.registrationNumber")}
              size='small'
              value={formData.registrationNumber}
              onChange={handleChange}
              autoComplete='off'
            />
          </Grid>

          <Grid size={6}>
            <TextField
              style={{ width: '100%' }}
              name='vatNumber'
              label={t("createMerchant.form.vat")}
              size='small'
              value={formData.vatNumber}
              InputLabelProps={{ shrink: !!formData.vatNumber }}
              onChange={handleChange}
              autoComplete='off'
            />
          </Grid>

          <Grid size={6}>
            <TextField
              style={{ width: '100%' }}
              name='website'
              label={t("createMerchant.form.website")}
              size='small'
              value={formData.website}
              InputLabelProps={{ shrink: !!formData.website }}
              onChange={handleChange}
              autoComplete='off'
            />
          </Grid>

          <Grid size={6}>
            <TextField
              style={{ width: '100%' }}
              name='category'
              label={t("createMerchant.form.category")}
              size='small'
              value={formData.category}
              InputLabelProps={{ shrink: !!formData.category }}
              onChange={handleChange}
              autoComplete='off'
            />
          </Grid>
        </Grid>
      </Box>
    );
  },
);

export default DetailsEnterprise;
